/**
 * CADMetadataPanel - Displays geometric metadata of a CAD model
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { File, Rotate3d, Ruler } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type { DefinitionGridItem } from '../common/DefinitionGrid';
import { CADMetadata } from '../../types/cad';
import { ConfidenceBadge } from './metadata/ConfidenceBadge';
import { MetadataSection } from './metadata/MetadataSection';
import { formatBytes, formatNumber } from './metadata/formatMetadata';

interface CADMetadataPanelProps {
  metadata: CADMetadata | null;
  filename?: string;
}

const SECTION_ICON = 'size-4 shrink-0 text-muted-foreground';

const CADMetadataPanel: React.FC<CADMetadataPanelProps> = ({ metadata }) => {
  const { t } = useTranslation();

  if (!metadata) {
    return (
      <div className="p-4 text-center">
        <p className="revy-cjk text-sm leading-[1.5] text-muted-foreground">
          {t('cad.metadata.empty')}
        </p>
      </div>
    );
  }

  const { wallThickness, meshQuality, draftRisk, geometryAnalysis } = metadata;

  const sections: { icon: React.ReactNode; title: string; rows: DefinitionGridItem[] }[] = [
    {
      icon: <Ruler strokeWidth={1.8} className={SECTION_ICON} />,
      title: t('cad.metadata.dimensions'),
      rows: [
        {
          label: t('cad.metadata.boundingBox'),
          value: `${metadata.boundingBox.x.toFixed(1)} × ${metadata.boundingBox.y.toFixed(1)} × ${metadata.boundingBox.z.toFixed(1)} mm`,
        },
        ...(metadata.volume != null
          ? [{ label: t('cad.metadata.volume'), value: `${(metadata.volume / 1000).toFixed(1)} cm³` }]
          : []),
        ...(metadata.surfaceArea != null
          ? [{ label: t('cad.metadata.surfaceArea'), value: `${metadata.surfaceArea.toFixed(1)} mm²` }]
          : []),
      ],
    },
    {
      icon: <Rotate3d strokeWidth={1.8} className={SECTION_ICON} />,
      title: t('cad.metadata.geometry'),
      rows: [
        { label: t('cad.metadata.triangleCount'), value: formatNumber(metadata.triangleCount) },
        { label: t('cad.metadata.vertexCount'), value: formatNumber(metadata.vertexCount) },
        { label: t('cad.metadata.edgeCount'), value: formatNumber(metadata.edgeCount) },
        { label: t('cad.metadata.faceCount'), value: formatNumber(metadata.faceCount) },
      ],
    },
    {
      icon: <File strokeWidth={1.8} className={SECTION_ICON} />,
      title: t('cad.metadata.file'),
      rows: [
        { label: t('cad.metadata.originalFormat'), value: metadata.originalFormat.toUpperCase() },
        {
          label: t('cad.metadata.originalFileSize'),
          value: formatBytes(metadata.originalFileSizeBytes),
        },
        { label: t('cad.metadata.glbSize'), value: formatBytes(metadata.glbFileSizeBytes) },
      ],
    },
  ];

  const wallThicknessRows: DefinitionGridItem[] =
    wallThickness && wallThickness.minMm != null
      ? [
          { label: t('cad.metadata.min'), value: `${wallThickness.minMm?.toFixed(2)} mm` },
          { label: t('cad.metadata.median'), value: `${wallThickness.medianMm?.toFixed(2)} mm` },
          { label: t('cad.metadata.mean'), value: `${wallThickness.meanMm?.toFixed(2)} mm` },
          { label: t('cad.metadata.max'), value: `${wallThickness.maxMm?.toFixed(2)} mm` },
        ...(wallThickness.hotspots?.length
          ? [
              {
                label: t('cad.metadata.thinAreas'),
                value: t('cad.metadata.detectedCount', { count: wallThickness.hotspots.length }),
              },
            ]
          : []),
        ]
      : [];

  const meshQualityRows: DefinitionGridItem[] = meshQuality
    ? [
        {
          label: t('cad.metadata.watertight'),
          value: meshQuality.isWatertight ? t('cad.metadata.yes') : t('cad.metadata.no'),
        },
        ...(meshQuality.bodyCount != null
          ? [{ label: t('cad.metadata.bodyCount'), value: meshQuality.bodyCount }]
          : []),
        ...(meshQuality.degenerateFaces != null && meshQuality.degenerateFaces > 0
          ? [
              {
                label: t('cad.metadata.degenerateFaces'),
                value: <span className="text-warning">{meshQuality.degenerateFaces}</span>,
              },
            ]
          : []),
      ]
    : [];

  const undercutPercent = (draftRisk?.undercutFraction as number) * 100;
  const undercutClass =
    undercutPercent > 15 ? 'text-destructive' : undercutPercent > 5 ? 'text-warning' : 'text-success';

  const draftRiskRows: DefinitionGridItem[] = draftRisk
    ? [
        {
          label: t('cad.metadata.undercutFraction'),
          value: <span className={undercutClass}>{undercutPercent.toFixed(1)}%</span>,
        },
        {
          label: t('cad.metadata.riskRegionCount'),
          value: t('cad.metadata.count', { count: draftRisk.riskRegions.length }),
        },
        {
          label: t('cad.metadata.pullDirection'),
          value: `[${draftRisk.pullDirection.map((v) => v.toFixed(2)).join(', ')}]`,
        },
      ]
    : [];

  return (
    <div>
      <h3 className="revy-cjk mb-2 text-sm font-semibold leading-[1.5]">
        {t('cad.metadata.title')}
      </h3>

      {metadata.volume == null && (
        <Badge variant="outline" className="revy-cjk mb-2 border-warning/60 font-normal text-warning">
          {t('cad.metadata.nonWatertightMesh')}
        </Badge>
      )}

      {sections.map((section, idx) => (
        <MetadataSection
          key={section.title}
          icon={section.icon}
          title={section.title}
          rows={section.rows}
          className={idx < sections.length - 1 ? 'mb-3' : undefined}
        />
      ))}

      {wallThickness && wallThickness.minMm != null && (
        <MetadataSection
          className="mt-3"
          title={t('cad.metadata.wallThickness')}
          badge={<ConfidenceBadge confidence={wallThickness.confidence} />}
          rows={wallThicknessRows}
        />
      )}

      {meshQuality && (
        <MetadataSection
          className="mt-3"
          title={t('cad.metadata.meshQuality')}
          rows={meshQualityRows}
        />
      )}

      {draftRisk && (
        <MetadataSection
          className="mt-3"
          title={t('cad.metadata.draftRisk')}
          badge={<ConfidenceBadge confidence={draftRisk.confidence} />}
          rows={draftRiskRows}
        />
      )}

      {geometryAnalysis?.analysisScope && (
        <MetadataSection
          className="mt-3"
          title={t('cad.metadata.analysisScope')}
          rows={[{ label: t('cad.metadata.target'), value: geometryAnalysis.analysisScope }]}
        />
      )}
    </div>
  );
};

export default CADMetadataPanel;
